import { useState } from "react";

const COLORS = {
  bg: "#F5F7FF",
  surface: "#FFFFFF",
  border: "#E2E8F8",
  accent: "#5B50E8",
  accentSoft: "#5B50E811",
  accentGlow: "#5B50E830",
  success: "#0DA882",
  successSoft: "#0DA88215",
  warning: "#D97706",
  warningSoft: "#D9770615",
  danger: "#E03355",
  dangerSoft: "#E0335515",
  textPrimary: "#1A1D35",
  textSecondary: "#4A5080",
  textMuted: "#9AA0C0",
};

// USERS — role: "admin" | "user"
const USERS = [
  { id: 1, name: "Sara K.",  avatar: "SK", color: "#6C63FF", role: "admin",  email: "sara@flowdesk.io",  password: "admin123" },
  { id: 2, name: "Tom R.",   avatar: "TR", color: "#22D3A5", role: "user",   email: "tom@flowdesk.io",   password: "tom123"   },
  { id: 3, name: "Mia L.",   avatar: "ML", color: "#F59E0B", role: "user",   email: "mia@flowdesk.io",   password: "mia123"   },
  { id: 4, name: "Dev P.",   avatar: "DP", color: "#F43F5E", role: "admin",  email: "dev@flowdesk.io",   password: "admin123" },
  { id: 5, name: "Jin W.",   avatar: "JW", color: "#38BDF8", role: "user",   email: "jin@flowdesk.io",   password: "jin123"   },
];

const CATEGORIES = ["Design", "Engineering", "Marketing", "Research", "Admin", "Other"];

const INITIAL_TASKS = [
  { id: 1, title: "Redesign landing page hero section", project: "Website Revamp", priority: "high", status: "inprogress", assignees: [1, 3], progress: 65, due: "Jun 12", tags: ["Design", "UI"], description: "Update hero with new brand visuals and CTA buttons.", category: "Design", remarksList: [
    { id: 1, memberId: 1, text: "Started wireframes, waiting on final brand assets from client.", timestamp: "Jun 9, 2026 · 9:14 AM" },
    { id: 2, memberId: 3, text: "Shared mood board with client for approval.", timestamp: "Jun 9, 2026 · 11:30 AM" },
  ]},
  { id: 2, title: "Set up CI/CD pipeline for staging", project: "DevOps", priority: "high", status: "inprogress", assignees: [2, 4], progress: 40, due: "Jun 11", tags: ["Backend", "Infra"], description: "Configure GitHub Actions for auto-deploy to staging.", category: "Engineering", remarksList: [
    { id: 1, memberId: 2, text: "Pipeline drafted. Blocked by missing AWS credentials.", timestamp: "Jun 8, 2026 · 3:45 PM" },
  ]},
  { id: 3, title: "Write onboarding email sequence", project: "Marketing", priority: "medium", status: "todo", assignees: [3], progress: 0, due: "Jun 15", tags: ["Copy", "Email"], description: "5-email drip sequence for new signups.", category: "Marketing", remarksList: [] },
  { id: 4, title: "API integration for payments", project: "FlowDesk App", priority: "high", status: "review", assignees: [4, 2, 5], progress: 90, due: "Jun 10", tags: ["Backend", "API"], description: "Stripe integration for subscription billing.", category: "Engineering", remarksList: [
    { id: 1, memberId: 4, text: "Webhook endpoints set up. Submitted for review.", timestamp: "Jun 9, 2026 · 8:00 AM" },
  ]},
  { id: 5, title: "User research interviews (5 users)", project: "Research", priority: "medium", status: "todo", assignees: [5, 1], progress: 0, due: "Jun 18", tags: ["Research"], description: "Conduct 30-min interviews to validate new feature ideas.", category: "Research", remarksList: [] },
  { id: 6, title: "Fix mobile nav overflow bug", project: "FlowDesk App", priority: "low", status: "done", assignees: [2], progress: 100, due: "Jun 9", tags: ["Frontend", "Bug"], description: "Nav items overflow on screens under 360px.", category: "Engineering", remarksList: [
    { id: 1, memberId: 2, text: "Fixed by adding overflow-x hidden on nav container.", timestamp: "Jun 9, 2026 · 7:50 AM" },
  ]},
  { id: 7, title: "Quarterly performance reviews", project: "HR", priority: "medium", status: "done", assignees: [1, 3, 4], progress: 100, due: "Jun 8", tags: ["Admin"], description: "Complete Q2 performance review forms for all team members.", category: "Admin", remarksList: [] },
  { id: 8, title: "Analytics dashboard charts", project: "FlowDesk App", priority: "medium", status: "inprogress", assignees: [5], progress: 30, due: "Jun 14", tags: ["Frontend"], description: "Build recharts-based dashboard with weekly/monthly toggle.", category: "Design", remarksList: [
    { id: 1, memberId: 5, text: "Chart library decided — using Recharts. Started on weekly view.", timestamp: "Jun 9, 2026 · 10:05 AM" },
  ]},
];

const PRIORITY_CONFIG = {
  high:   { label: "High", color: COLORS.danger,  bg: COLORS.dangerSoft  },
  medium: { label: "Med",  color: COLORS.warning, bg: COLORS.warningSoft },
  low:    { label: "Low",  color: COLORS.success, bg: COLORS.successSoft },
};
const STATUS_COLUMNS = [
  { id: "todo",       label: "To Do",       color: COLORS.textMuted },
  { id: "inprogress", label: "In Progress", color: COLORS.accent    },
  { id: "review",     label: "Review",      color: COLORS.warning   },
  { id: "done",       label: "Done",        color: COLORS.success   },
];

function formatTimestamp(date) {
  return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).replace(",", " ·");
}

// ─── SHARED UI ────────────────────────────────────────────────────────────────
function Avatar({ user, size = 32, overlap = false }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: user.color + "22", border: `2px solid ${overlap ? "#fff" : user.color + "66"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.34, fontWeight: 700, color: user.color, flexShrink: 0 }}>
      {user.avatar}
    </div>
  );
}

function AvatarStack({ assigneeIds, users, size = 26, max = 4 }) {
  const assigned = assigneeIds.map(id => users.find(m => m.id === id)).filter(Boolean);
  const visible = assigned.slice(0, max);
  const extra = assigned.length - max;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {visible.map((m, i) => (
        <div key={m.id} style={{ marginLeft: i === 0 ? 0 : -(size * 0.35), zIndex: visible.length - i }}>
          <Avatar user={m} size={size} overlap={i > 0} />
        </div>
      ))}
      {extra > 0 && <div style={{ width: size, height: size, borderRadius: "50%", marginLeft: -(size * 0.35), background: COLORS.border, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.32, fontWeight: 700, color: COLORS.textSecondary }}>+{extra}</div>}
    </div>
  );
}

function ProgressBar({ value, color = COLORS.accent, height = 4 }) {
  return (
    <div style={{ background: COLORS.border, borderRadius: 99, height, overflow: "hidden" }}>
      <div style={{ width: `${value}%`, height: "100%", borderRadius: 99, background: value === 100 ? COLORS.success : color, transition: "width 0.6s ease" }} />
    </div>
  );
}

function RoleBadge({ role }) {
  const isAdmin = role === "admin";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 99, background: isAdmin ? COLORS.accentSoft : COLORS.successSoft, color: isAdmin ? COLORS.accent : COLORS.success, letterSpacing: 0.5 }}>
      {isAdmin ? "ADMIN" : "USER"}
    </span>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 16px" }}>
      <div style={{ flex: 1, height: 1, background: COLORS.border }} />
      <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, letterSpacing: 0.8 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: COLORS.border }} />
    </div>
  );
}

function MemberPickerRow({ users, selected, onToggle }) {
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {users.map(m => {
        const isSelected = selected.includes(m.id);
        return (
          <div key={m.id} onClick={() => onToggle(m.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, cursor: "pointer", opacity: isSelected ? 1 : 0.4, transform: isSelected ? "scale(1.1)" : "scale(1)", transition: "all 0.2s" }}>
            <div style={{ position: "relative" }}>
              <Avatar user={m} size={38} />
              {isSelected && <div style={{ position: "absolute", bottom: -2, right: -2, width: 14, height: 14, borderRadius: "50%", background: COLORS.accent, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: "#fff", fontWeight: 800 }}>✓</div>}
            </div>
            <span style={{ fontSize: 10, color: isSelected ? COLORS.textPrimary : COLORS.textMuted, fontWeight: isSelected ? 700 : 400 }}>{m.name.split(" ")[0]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, users }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  const handleLogin = () => {
    if (!email.trim()) { setError("Email is required."); return; }
    const user = users.find(u => u.email === email.trim().toLowerCase() && u.password === password);
    if (user) { setError(""); onLogin(user); }
    else setError("Incorrect email or password.");
  };

  const handleForgot = () => {
    if (!forgotEmail.trim()) { setForgotMsg("Please enter your email address."); return; }
    const user = users.find(u => u.email === forgotEmail.trim().toLowerCase());
    if (user) setForgotMsg("Password hint: " + user.password);
    else setForgotMsg("No account found with that email.");
  };

  const inputStyle = { width: "100%", padding: "13px 16px", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textPrimary, fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s" };

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, background: COLORS.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16, boxShadow: `0 8px 24px ${COLORS.accentGlow}` }}>
            <span style={{ fontSize: 24, color: "#fff" }}>◈</span>
          </div>
          <div style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary }}>FlowDesk</div>
          <div style={{ fontSize: 14, color: COLORS.textMuted, marginTop: 4 }}>Sign in to your workspace</div>
        </div>

        <div style={{ background: COLORS.surface, borderRadius: 20, padding: "28px 24px", border: `1px solid ${COLORS.border}`, boxShadow: "0 4px 24px #5B50E808" }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7, letterSpacing: 0.5 }}>EMAIL</div>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="you@flowdesk.io" type="email" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border}
              onKeyDown={e => e.key === "Enter" && handleLogin()} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7, letterSpacing: 0.5 }}>PASSWORD</div>
            <div style={{ position: "relative" }}>
              <input value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" type={showPw ? "text" : "password"} style={{ ...inputStyle, paddingRight: 46 }}
                onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border}
                onKeyDown={e => e.key === "Enter" && handleLogin()} />
              <button onClick={() => setShowPw(p => !p)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 16, color: COLORS.textMuted }}>
                {showPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>
          {error && <div style={{ fontSize: 12, color: COLORS.danger, background: COLORS.dangerSoft, padding: "10px 14px", borderRadius: 10, marginBottom: 16 }}>⚠ {error}</div>}
          <button onClick={handleLogin} style={{ width: "100%", padding: "14px", borderRadius: 13, border: "none", background: COLORS.accent, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: `0 4px 16px ${COLORS.accentGlow}` }}>
            Sign In
          </button>
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <span onClick={() => { setForgotMode(true); setForgotMsg(""); setForgotEmail(""); }} style={{ fontSize: 13, color: COLORS.accent, cursor: "pointer", fontWeight: 600 }}>Forgot password?</span>
          </div>
        </div>
      </div>
      {forgotMode && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 24 }}>
          <div style={{ background: COLORS.surface, borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 360, boxShadow: "0 8px 40px #0002" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.textPrimary, marginBottom: 6 }}>Forgot Password</div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 18 }}>Enter your email address to retrieve your password hint.</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7, letterSpacing: 0.5 }}>EMAIL ADDRESS</div>
            <input value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="you@flowdesk.io" type="email" style={{ width: "100%", padding: "13px 16px", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textPrimary, fontSize: 15, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 14 }} onKeyDown={e => e.key === "Enter" && handleForgot()} />
            {forgotMsg && <div style={{ fontSize: 13, padding: "10px 14px", borderRadius: 10, marginBottom: 14, background: forgotMsg.startsWith("Password") ? COLORS.successSoft : COLORS.dangerSoft, color: forgotMsg.startsWith("Password") ? COLORS.success : COLORS.danger }}>{forgotMsg}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setForgotMode(false)} style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: "none", color: COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleForgot} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: COLORS.accent, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TASK CARD ─────────────────────────────────────────────────────────────────
function TaskCard({ task, users, onClick, currentUser }) {
  const prio = PRIORITY_CONFIG[task.priority];
  const statusColor = task.status === "done" ? COLORS.success : task.status === "review" ? COLORS.warning : task.status === "inprogress" ? COLORS.accent : COLORS.border;
  const latest = task.remarksList?.[task.remarksList.length - 1];
  const latestMember = latest ? users.find(x => x.id === latest.memberId) : null;
  return (
    <div onClick={() => onClick(task)} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px", cursor: "pointer", transition: "box-shadow 0.2s, transform 0.15s", borderLeft: `3px solid ${statusColor}` }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 20px ${COLORS.accentGlow}`; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: prio.color, background: prio.bg, padding: "3px 8px", borderRadius: 99 }}>{prio.label}</span>
          {task.category && <span style={{ fontSize: 10, color: COLORS.textMuted, background: COLORS.border, padding: "3px 7px", borderRadius: 99 }}>{task.category}</span>}
        </div>
        <span style={{ fontSize: 11, color: COLORS.textMuted }}>Due {task.due}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, marginBottom: 4, lineHeight: 1.4 }}>{task.title}</div>
      {task.description && <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 6, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{task.description}</div>}
      {task.status === "inprogress" && latest && (
        <div style={{ fontSize: 11, background: COLORS.warningSoft, padding: "7px 10px", borderRadius: 8, marginBottom: 8, borderLeft: `3px solid ${COLORS.warning}` }}>
          <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 2 }}>
            <span>💬</span>
            <span style={{ fontWeight: 700, color: latestMember ? latestMember.color : COLORS.textPrimary }}>{latestMember ? latestMember.name : "?"}</span>
            <span style={{ color: COLORS.textMuted }}>{latest.timestamp}</span>
          </div>
          <span style={{ color: COLORS.textSecondary }}>{latest.text}</span>
        </div>
      )}
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>{task.project}</div>
      <div style={{ marginBottom: 10 }}>
        <ProgressBar value={task.progress} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
          <span style={{ fontSize: 11, color: COLORS.textMuted }}>Progress</span>
          <span style={{ fontSize: 11, color: task.progress === 100 ? COLORS.success : COLORS.textSecondary, fontWeight: 600 }}>{task.progress}%</span>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 4 }}>
          {task.tags.slice(0, 2).map(tag => <span key={tag} style={{ fontSize: 10, color: COLORS.accent, background: COLORS.accentSoft, padding: "2px 7px", borderRadius: 99, fontWeight: 500 }}>{tag}</span>)}
        </div>
        <AvatarStack assigneeIds={task.assignees} users={users} size={24} />
      </div>
    </div>
  );
}

// ─── REMARK ENTRY ──────────────────────────────────────────────────────────────
function RemarkEntry({ entry, users }) {
  const member = users.find(m => m.id === entry.memberId);
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
      {member && <div style={{ flexShrink: 0, marginTop: 2 }}><Avatar user={member} size={28} /></div>}
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: member ? member.color : COLORS.textPrimary }}>{member ? member.name : "Unknown"}</span>
          <RoleBadge role={member?.role || "user"} />
          <span style={{ fontSize: 10, color: COLORS.textMuted }}>{entry.timestamp}</span>
        </div>
        <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.55, background: COLORS.bg, padding: "10px 13px", borderRadius: "4px 12px 12px 12px", border: `1px solid ${COLORS.border}` }}>
          {entry.text}
        </div>
      </div>
    </div>
  );
}

// ─── TASK MODAL ────────────────────────────────────────────────────────────────
function TaskModal({ task, users, onClose, onUpdate, currentUser }) {
  const [prog, setProg] = useState(task.progress);
  const [status, setStatus] = useState(task.status);
  const [assignees, setAssignees] = useState(task.assignees);
  const [remarksList, setRemarksList] = useState(task.remarksList || []);
  const [newRemark, setNewRemark] = useState("");
  const prio = PRIORITY_CONFIG[task.priority];
  const isAdmin = currentUser.role === "admin";
  const isInProgress = status === "inprogress";

  const toggleAssignee = (id) => {
    if (!isAdmin) return;
    setAssignees(prev => prev.includes(id) ? (prev.length > 1 ? prev.filter(a => a !== id) : prev) : [...prev, id]);
  };

  const addRemark = () => {
    if (!newRemark.trim()) return;
    setRemarksList(prev => [...prev, { id: Date.now(), memberId: currentUser.id, text: newRemark.trim(), timestamp: formatTimestamp(new Date()) }]);
    setNewRemark("");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000050", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: COLORS.surface, borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 600, padding: "28px 24px 40px", border: `1px solid ${COLORS.border}`, borderBottom: "none", maxHeight: "92vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 40, height: 4, background: COLORS.border, borderRadius: 99, margin: "0 auto 24px" }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ flex: 1, paddingRight: 12 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>{task.project}</span>
              {task.category && <span style={{ fontSize: 11, color: COLORS.accent, background: COLORS.accentSoft, padding: "1px 7px", borderRadius: 99 }}>{task.category}</span>}
            </div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: COLORS.textPrimary, margin: 0, lineHeight: 1.3 }}>{task.title}</h2>
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: prio.color, background: prio.bg, padding: "4px 10px", borderRadius: 99, whiteSpace: "nowrap" }}>{prio.label}</span>
        </div>

        {task.description && (
          <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6, padding: "12px 14px", background: COLORS.bg, borderRadius: 10, marginBottom: 18 }}>
            {task.description}
          </div>
        )}

        {/* ASSIGNEES — admin can edit, user sees read-only */}
        <div style={{ marginBottom: 18, padding: "14px", background: COLORS.bg, borderRadius: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 700 }}>ASSIGNED TO</span>
            {isAdmin
              ? <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 700 }}>{assignees.length} member{assignees.length !== 1 ? "s" : ""} · tap to change</span>
              : <span style={{ fontSize: 11, color: COLORS.textMuted }}>view only</span>
            }
          </div>
          {isAdmin
            ? <MemberPickerRow users={users} selected={assignees} onToggle={toggleAssignee} />
            : <div style={{ display: "flex", gap: 8 }}>{assignees.map(id => { const u = users.find(x => x.id === id); return u ? <Avatar key={id} user={u} size={34} /> : null; })}</div>
          }
        </div>

        {/* STATUS — admin only can change */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 700 }}>STATUS</span>
            {!isAdmin && <span style={{ fontSize: 11, color: COLORS.textMuted }}>view only</span>}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {STATUS_COLUMNS.map(s => (
              <button key={s.id} onClick={() => isAdmin && setStatus(s.id)} style={{
                flex: 1, padding: "8px 4px", borderRadius: 10, border: `1.5px solid`,
                borderColor: status === s.id ? s.color : COLORS.border,
                background: status === s.id ? s.color + "15" : "transparent",
                color: status === s.id ? s.color : COLORS.textMuted,
                fontSize: 10, fontWeight: 700, cursor: isAdmin ? "pointer" : "default",
                opacity: !isAdmin && status !== s.id ? 0.5 : 1,
                transition: "all 0.2s",
              }}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* PROGRESS — admin can drag, user sees bar only */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 700 }}>PROGRESS</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: prog === 100 ? COLORS.success : COLORS.textPrimary }}>{prog}%</span>
          </div>
          <ProgressBar value={prog} height={8} />
          {isAdmin && (
            <input type="range" min={0} max={100} value={prog} onChange={e => setProg(Number(e.target.value))}
              style={{ width: "100%", marginTop: 10, accentColor: COLORS.accent, cursor: "pointer" }} />
          )}
        </div>

        {/* REMARKS LOG */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 700 }}>💬 REMARKS</span>
              {remarksList.length > 0 && <span style={{ fontSize: 10, background: COLORS.accentSoft, color: COLORS.accent, padding: "2px 8px", borderRadius: 99, fontWeight: 700 }}>{remarksList.length}</span>}
            </div>
            {isInProgress && <span style={{ fontSize: 10, color: COLORS.warning, background: COLORS.warningSoft, padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>In Progress</span>}
          </div>
          {remarksList.length > 0
            ? <div style={{ marginBottom: 14 }}>{remarksList.map(e => <RemarkEntry key={e.id} entry={e} users={users} />)}</div>
            : <div style={{ textAlign: "center", padding: "14px", color: COLORS.textMuted, fontSize: 12, background: COLORS.bg, borderRadius: 12, marginBottom: 14 }}>No remarks yet.</div>
          }
          {/* New remark — available to all */}
          <div style={{ border: `1.5px solid ${isInProgress ? COLORS.warning + "55" : COLORS.border}`, borderRadius: 14, overflow: "hidden", background: isInProgress ? COLORS.warningSoft : COLORS.bg }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 12px 0" }}>
              <div style={{ marginTop: 2, flexShrink: 0 }}><Avatar user={currentUser} size={28} /></div>
              <textarea value={newRemark} onChange={e => setNewRemark(e.target.value)}
                placeholder={isInProgress ? "Add a progress update, blocker, or note..." : "Add a remark..."}
                rows={2} style={{ flex: 1, border: "none", background: "transparent", outline: "none", fontSize: 13, color: COLORS.textPrimary, fontFamily: "inherit", lineHeight: 1.5, resize: "none", paddingTop: 4 }}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addRemark(); } }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px 10px" }}>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>As <strong style={{ color: currentUser.color }}>{currentUser.name}</strong> · Enter to post</span>
              <button onClick={addRemark} disabled={!newRemark.trim()} style={{ padding: "6px 14px", borderRadius: 99, border: "none", cursor: newRemark.trim() ? "pointer" : "default", background: newRemark.trim() ? COLORS.accent : COLORS.border, color: newRemark.trim() ? "#fff" : COLORS.textMuted, fontSize: 12, fontWeight: 700, transition: "all 0.2s" }}>Post</button>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Close</button>
          {isAdmin && (
            <button onClick={() => { onUpdate(task.id, prog, status, assignees, remarksList); onClose(); }} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: COLORS.accent, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14, boxShadow: `0 4px 16px ${COLORS.accentGlow}` }}>Save Changes</button>
          )}
          {!isAdmin && (
            <button onClick={() => { onUpdate(task.id, prog, status, assignees, remarksList); onClose(); }} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: COLORS.success, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Save Remarks</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ADD TASK MODAL ────────────────────────────────────────────────────────────
function AddTaskModal({ users, onClose, onAdd, currentUser }) {
  const isAdmin = currentUser.role === "admin";
  const [form, setForm] = useState({
    title: "", project: "", description: "", category: "Engineering",
    priority: "medium",
    assignees: isAdmin ? [currentUser.id] : [currentUser.id],
    due: "", tags: "", estimatedHours: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleAssignee = (id) => {
    setForm(f => ({ ...f, assignees: f.assignees.includes(id) ? (f.assignees.length > 1 ? f.assignees.filter(a => a !== id) : f.assignees) : [...f.assignees, id] }));
  };
  const inputStyle = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textPrimary, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={onClose}>
      <div style={{ background: COLORS.surface, borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 600, padding: "24px 24px 40px", border: `1px solid ${COLORS.border}`, borderBottom: "none", maxHeight: "93vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 40, height: 4, background: COLORS.border, borderRadius: 99, margin: "0 auto 20px" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: 19, fontWeight: 800, color: COLORS.textPrimary, margin: 0 }}>New Task</h2>
          <RoleBadge role={currentUser.role} />
        </div>

        <SectionDivider label="BASIC INFO" />
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>TASK TITLE <span style={{ color: COLORS.danger }}>*</span></div>
          <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="What needs to be done?" style={inputStyle}
            onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>DESCRIPTION</div>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Describe the task..." rows={3}
            style={{ ...inputStyle, resize: "none" }} onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>PROJECT <span style={{ color: COLORS.danger }}>*</span></div>
            <input value={form.project} onChange={e => set("project", e.target.value)} placeholder="Project name" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>EST. HOURS</div>
            <input type="number" value={form.estimatedHours} onChange={e => set("estimatedHours", e.target.value)} placeholder="e.g. 8" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>DUE DATE</div>
            <input value={form.due} onChange={e => set("due", e.target.value)} placeholder="e.g. Jun 20" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 7 }}>TAGS</div>
            <input value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="Design, Bug" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent} onBlur={e => e.target.style.borderColor = COLORS.border} />
          </div>
        </div>

        <SectionDivider label="CLASSIFICATION" />
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 8 }}>CATEGORY</div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {CATEGORIES.map(c => <button key={c} onClick={() => set("category", c)} style={{ padding: "7px 13px", borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1.5px solid ${form.category === c ? COLORS.accent : COLORS.border}`, background: form.category === c ? COLORS.accentSoft : "transparent", color: form.category === c ? COLORS.accent : COLORS.textMuted, transition: "all 0.15s" }}>{c}</button>)}
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 8 }}>PRIORITY</div>
          <div style={{ display: "flex", gap: 8 }}>
            {["high", "medium", "low"].map(p => { const pc = PRIORITY_CONFIG[p]; return <button key={p} onClick={() => set("priority", p)} style={{ flex: 1, padding: "10px 4px", borderRadius: 10, border: `1.5px solid ${form.priority === p ? pc.color : COLORS.border}`, background: form.priority === p ? pc.bg : "transparent", color: form.priority === p ? pc.color : COLORS.textMuted, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>{pc.label}</button>; })}
          </div>
        </div>

        <SectionDivider label="ASSIGNMENT" />
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700 }}>ASSIGN TO <span style={{ color: COLORS.danger }}>*</span></div>
            <span style={{ fontSize: 12, color: COLORS.accent, fontWeight: 700, background: COLORS.accentSoft, padding: "3px 10px", borderRadius: 99 }}>{form.assignees.length} selected</span>
          </div>
          {isAdmin
            ? <MemberPickerRow users={users} selected={form.assignees} onToggle={toggleAssignee} />
            : (
              <div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Avatar user={currentUser} size={36} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary }}>{currentUser.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textMuted }}>Task assigned to you</div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: 12, color: COLORS.textMuted, background: COLORS.bg, padding: "10px 12px", borderRadius: 10 }}>
                  ℹ Only admins can assign tasks to other members.
                </div>
              </div>
            )
          }
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.textSecondary, fontWeight: 600, cursor: "pointer", fontSize: 14 }}>Cancel</button>
          <button onClick={() => { if (form.title && form.project) { onAdd(form); onClose(); } }} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: form.title && form.project ? COLORS.accent : COLORS.border, color: form.title && form.project ? "#fff" : COLORS.textMuted, fontWeight: 700, cursor: "pointer", fontSize: 14, boxShadow: form.title && form.project ? `0 4px 16px ${COLORS.accentGlow}` : "none" }}>
            Create Task
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN: USER MANAGEMENT ────────────────────────────────────────────────────
function UserManagementView({ users, tasks, onDeleteUser, onAddUser, currentUser }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("user");
  const [addError, setAddError] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editRole, setEditRole] = useState("user");
  const [editError, setEditError] = useState("");

  const avatarFromName = n => n.trim().split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2);
  const colors = ["#6C63FF","#22D3A5","#F59E0B","#F43F5E","#38BDF8","#10B981","#8B5CF6"];

  const handleAdd = () => {
    if (!newName.trim()) { setAddError("Name is required."); return; }
    if (!newEmail.trim() || !newEmail.includes("@")) { setAddError("Valid email is required."); return; }
    if (!newPassword.trim()) { setAddError("Password is required."); return; }
    if (users.find(u => u.email === newEmail.trim().toLowerCase())) { setAddError("Email already exists."); return; }
    const newUser = { id: Date.now(), name: newName.trim(), avatar: avatarFromName(newName), color: colors[users.length % colors.length], role: newRole, email: newEmail.trim().toLowerCase(), password: newPassword.trim() };
    onAddUser(newUser);
    setNewName(""); setNewEmail(""); setNewPassword(""); setNewRole("user"); setAddError(""); setShowAdd(false);
  };

  const inputStyle = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${COLORS.border}`, background: COLORS.bg, color: COLORS.textPrimary, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 12 };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: COLORS.textMuted }}>{users.length} members in workspace</div>
        <button onClick={() => setShowAdd(true)} style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: COLORS.accent, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>+ Add User</button>
      </div>
      {users.map(u => {
        const userTasks = tasks.filter(t => t.assignees.includes(u.id));
        const active = userTasks.filter(t => t.status !== "done").length;
        const done = userTasks.filter(t => t.status === "done").length;
        return (
          <div key={u.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "16px 18px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <Avatar user={u} size={44} />
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{u.name}</span>
                  <RoleBadge role={u.role} />
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>{u.email}</div>
              </div>
              {u.id !== currentUser.id && (
                <button onClick={() => { if (window.confirm("Delete " + u.name + "?")) onDeleteUser(u.id); }} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${COLORS.danger}`, background: COLORS.dangerSoft, color: COLORS.danger, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Delete</button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[{ label: "Total", val: userTasks.length, color: COLORS.textPrimary }, { label: "Active", val: active, color: COLORS.accent }, { label: "Done", val: done, color: COLORS.success }].map(s => (
                <div key={s.label} style={{ background: COLORS.bg, borderRadius: 10, padding: "10px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <ProgressBar value={userTasks.length > 0 ? Math.round((done / userTasks.length) * 100) : 0} color={u.color} height={5} />
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{userTasks.length > 0 ? Math.round((done / userTasks.length) * 100) : 0}% completion</div>
            </div>
          </div>
        );
      })}
      {showAdd && (
        <div style={{ position: "fixed", inset: 0, background: "#0006", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 24 }}>
          <div style={{ background: COLORS.surface, borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 400, boxShadow: "0 8px 40px #0002" }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.textPrimary, marginBottom: 18 }}>Add New User</div>
            {addError && <div style={{ fontSize: 12, color: COLORS.danger, background: COLORS.dangerSoft, padding: "10px 14px", borderRadius: 10, marginBottom: 14 }}>{addError}</div>}
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>FULL NAME</div>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Alex M." style={inputStyle} />
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>EMAIL ADDRESS</div>
            <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="alex@flowdesk.io" type="email" style={inputStyle} />
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>PASSWORD</div>
            <input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="password" type="text" style={inputStyle} />
            <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 700, marginBottom: 6, letterSpacing: 0.5 }}>ROLE</div>
            <select value={newRole} onChange={e => setNewRole(e.target.value)} style={{ ...inputStyle, marginBottom: 20 }}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setShowAdd(false); setAddError(""); }} style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1.5px solid ${COLORS.border}`, background: "none", color: COLORS.textSecondary, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleAdd} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: COLORS.accent, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Create User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────────
export default function FlowDesk() {
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState(USERS);
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [view, setView] = useState("dashboard");
  const [selectedTask, setSelectedTask] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [dashDrill, setDashDrill] = useState(null);

  if (!currentUser) return <LoginScreen users={users} onLogin={u => { setCurrentUser(u); setView(u.role === "admin" ? "dashboard" : "tasks"); }} />;

  const isAdmin = currentUser.role === "admin";

  // Permission filter: users see only their tasks, admins see all
  const visibleTasks = isAdmin ? tasks : tasks.filter(t => t.assignees.includes(currentUser.id));

  const stats = {
    total: visibleTasks.length,
    inprogress: visibleTasks.filter(t => t.status === "inprogress").length,
    done: visibleTasks.filter(t => t.status === "done").length,
    overdue: visibleTasks.filter(t => t.priority === "high" && t.status !== "done").length,
  };

  const handleUpdate = (id, progress, status, assignees, remarksList) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, progress, status, assignees, remarksList } : t));
  };

  const handleAdd = (form) => {
    const newTask = {
      id: Date.now(), title: form.title, project: form.project,
      priority: form.priority, status: "todo",
      assignees: isAdmin ? form.assignees : [currentUser.id],
      progress: 0, due: form.due || "TBD",
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      description: form.description, category: form.category,
      estimatedHours: form.estimatedHours, remarksList: [],
    };
    setTasks(prev => [newTask, ...prev]);
  };

  const getDrillTasks = (drill) => {
    if (!drill) return [];
    if (drill === "total") return visibleTasks;
    if (drill === "inprogress") return visibleTasks.filter(t => t.status === "inprogress");
    if (drill === "done") return visibleTasks.filter(t => t.status === "done");
    if (drill === "highpriority") return visibleTasks.filter(t => t.priority === "high" && t.status !== "done");
    return visibleTasks.filter(t => t.status === drill);
  };

  const getDrillTitle = (drill) => {
    if (drill === "total") return "All Tasks"; if (drill === "inprogress") return "In Progress";
    if (drill === "done") return "Completed"; if (drill === "highpriority") return "High Priority";
    return STATUS_COLUMNS.find(s => s.id === drill)?.label || drill;
  };

  const filtered = filterStatus === "all" ? visibleTasks : visibleTasks.filter(t => t.status === filterStatus);
  const teamLoad = USERS.map(m => ({ ...m, tasks: tasks.filter(t => t.assignees.includes(m.id) && t.status !== "done").length, done: tasks.filter(t => t.assignees.includes(m.id) && t.status === "done").length }));

  // Nav tabs based on role
  const navTabs = isAdmin
    ? [{ id: "dashboard", label: "Dashboard", icon: "◈" }, { id: "tasks", label: "Tasks", icon: "☰" }, { id: "team", label: "Team", icon: "◎" }, { id: "users", label: "Users", icon: "👥" }]
    : [{ id: "tasks", label: "My Tasks", icon: "☰" }];

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", fontFamily: "'Inter', -apple-system, sans-serif", color: COLORS.textPrimary, maxWidth: 1280, margin: "0 auto", position: "relative" }}>

      {/* Header */}
      <div style={{ padding: "20px 20px 0", position: "sticky", top: 0, background: COLORS.bg, zIndex: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.accent, fontWeight: 700, letterSpacing: 2, marginBottom: 2 }}>FLOWDESK</div>
            <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>
              {view === "dashboard" ? "Overview" : view === "tasks" ? (isAdmin ? "All Tasks" : "My Tasks") : view === "team" ? "Team" : "Users"}
            </h1>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={() => setShowAdd(true)} style={{ width: 40, height: 40, borderRadius: 12, border: "none", background: COLORS.accent, color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${COLORS.accentGlow}` }}>+</button>
            {/* User avatar + logout */}
            <div style={{ position: "relative" }}>
              <div onClick={() => setCurrentUser(null)} title="Sign out" style={{ cursor: "pointer" }}>
                <Avatar user={currentUser} size={40} />
              </div>
              <div style={{ position: "absolute", bottom: -3, right: -3, width: 14, height: 14, borderRadius: "50%", background: isAdmin ? COLORS.accent : COLORS.success, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, color: "#fff", fontWeight: 800 }}>
                {isAdmin ? "A" : "U"}
              </div>
            </div>
          </div>
        </div>

        {/* Role banner for non-admins */}
        {!isAdmin && (
          <div style={{ background: COLORS.successSoft, border: `1px solid ${COLORS.success}33`, borderRadius: 10, padding: "8px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13 }}>👤</span>
            <span style={{ fontSize: 12, color: COLORS.success, fontWeight: 600 }}>Viewing your assigned tasks only</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 14, scrollbarWidth: "none" }}>
          {navTabs.map(tab => (
            <button key={tab.id} onClick={() => { setView(tab.id); setDashDrill(null); }} style={{ padding: "8px 14px", borderRadius: 99, border: "none", cursor: "pointer", whiteSpace: "nowrap", background: view === tab.id ? COLORS.accent : COLORS.surface, color: view === tab.id ? "#fff" : COLORS.textSecondary, fontWeight: view === tab.id ? 700 : 500, fontSize: 12, boxShadow: view === tab.id ? `0 2px 12px ${COLORS.accentGlow}` : "none" }}>{tab.icon} {tab.label}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "0 20px 100px" }}>

        {/* ── ADMIN: DASHBOARD ── */}
        {view === "dashboard" && isAdmin && !dashDrill && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              {[
                { key: "total",       label: "Total Tasks",   value: stats.total,     icon: "📋", color: COLORS.textPrimary },
                { key: "inprogress",  label: "In Progress",   value: stats.inprogress, icon: "⚡", color: COLORS.accent },
                { key: "done",        label: "Completed",     value: stats.done,       icon: "✓", color: COLORS.success },
                { key: "highpriority",label: "High Priority", value: stats.overdue,    icon: "🔴", color: COLORS.danger },
              ].map(card => (
                <div key={card.key} onClick={() => setDashDrill(card.key)} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "20px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 8, transition: "box-shadow 0.2s, transform 0.15s", position: "relative", overflow: "hidden" }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 6px 24px ${card.color}22`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                  <div style={{ position: "absolute", top: 0, right: 0, width: 60, height: 60, borderRadius: "0 16px 0 60px", background: card.color + "10" }} />
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: COLORS.textSecondary, fontWeight: 500 }}>{card.label}</span>
                    <span style={{ fontSize: 20 }}>{card.icon}</span>
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.value}</div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>tap to view</span>
                    <span style={{ fontSize: 11, color: card.color, fontWeight: 600 }}>View →</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "20px", marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Overall Progress</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.success }}>{Math.round((stats.done / Math.max(stats.total, 1)) * 100)}%</span>
              </div>
              <div style={{ display: "flex", height: 10, borderRadius: 99, overflow: "hidden", gap: 2, marginBottom: 14 }}>
                {STATUS_COLUMNS.map(s => { const count = tasks.filter(t => t.status === s.id).length; const pct = (count / Math.max(tasks.length, 1)) * 100; return pct > 0 ? <div key={s.id} onClick={() => setDashDrill(s.id)} title={`${s.label}: ${count}`} style={{ width: `${pct}%`, background: s.color, cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.opacity = "0.7"} onMouseLeave={e => e.currentTarget.style.opacity = "1"} /> : null; })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {STATUS_COLUMNS.map(s => { const count = tasks.filter(t => t.status === s.id).length; return <div key={s.id} onClick={() => setDashDrill(s.id)} style={{ textAlign: "center", cursor: "pointer", padding: "6px 8px", borderRadius: 10, transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = s.color + "15"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}><div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{count}</div><div style={{ fontSize: 10, color: COLORS.textMuted }}>{s.label}</div></div>; })}
              </div>
            </div>

            <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "20px", marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Team Load</span>
                <button onClick={() => setView("team")} style={{ fontSize: 12, color: COLORS.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>See all →</button>
              </div>
              {teamLoad.map(m => (
                <div key={m.id} onClick={() => setView("team")} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "6px 8px", borderRadius: 10, marginBottom: 4, transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = COLORS.bg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <Avatar user={m} size={30} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                    <div style={{ height: 4, background: COLORS.border, borderRadius: 99, marginTop: 4, overflow: "hidden" }}>
                      <div style={{ width: `${m.tasks + m.done > 0 ? Math.round((m.done / (m.tasks + m.done)) * 100) : 0}%`, height: "100%", background: m.color, borderRadius: 99 }} />
                    </div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: m.tasks > 2 ? COLORS.danger : m.tasks > 0 ? COLORS.accent : COLORS.success }}>{m.tasks} active</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DRILL-DOWN */}
        {view === "dashboard" && isAdmin && dashDrill && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <button onClick={() => setDashDrill(null)} style={{ padding: "8px 14px", borderRadius: 99, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>← Back</button>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{getDrillTitle(dashDrill)}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>{getDrillTasks(dashDrill).length} tasks</div>
              </div>
            </div>
            {getDrillTasks(dashDrill).length === 0
              ? <div style={{ textAlign: "center", padding: "48px 24px", color: COLORS.textMuted }}><div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div><div style={{ fontWeight: 600 }}>No tasks here</div></div>
              : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{getDrillTasks(dashDrill).map(task => <TaskCard key={task.id} task={task} users={USERS} onClick={setSelectedTask} currentUser={currentUser} />)}</div>
            }
          </div>
        )}

        {/* TASKS VIEW */}
        {view === "tasks" && (
          <div>
            {!isAdmin && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "12px 14px", background: COLORS.surface, borderRadius: 14, border: `1px solid ${COLORS.border}` }}>
                <Avatar user={currentUser} size={36} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{currentUser.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>{filtered.length} task{filtered.length !== 1 ? "s" : ""} assigned to you</div>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto", paddingBottom: 4, scrollbarWidth: "none" }}>
              {[{ id: "all", label: "All" }, ...STATUS_COLUMNS].map(s => (
                <button key={s.id} onClick={() => setFilterStatus(s.id)} style={{ padding: "7px 14px", borderRadius: 99, border: `1px solid ${filterStatus === s.id ? (s.color || COLORS.accent) : COLORS.border}`, background: filterStatus === s.id ? ((s.color || COLORS.accent) + "15") : COLORS.surface, color: filterStatus === s.id ? (s.color || COLORS.accent) : COLORS.textMuted, fontWeight: 600, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {s.label} {s.id !== "all" && <span style={{ opacity: 0.7 }}>({visibleTasks.filter(t => t.status === s.id).length})</span>}
                </button>
              ))}
            </div>
            {filtered.length === 0
              ? <div style={{ textAlign: "center", padding: "48px 24px", color: COLORS.textMuted }}><div style={{ fontSize: 36, marginBottom: 12 }}>📋</div><div style={{ fontWeight: 600 }}>No tasks found</div></div>
              : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{filtered.map(task => <TaskCard key={task.id} task={task} users={USERS} onClick={setSelectedTask} currentUser={currentUser} />)}</div>
            }
          </div>
        )}

        {/* TEAM VIEW — admin only */}
        {view === "team" && isAdmin && (
          <div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 14 }}>Workload across all members</div>
            {teamLoad.map(m => (
              <div key={m.id} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "16px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <Avatar user={m} size={42} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}><div style={{ fontWeight: 700, fontSize: 15 }}>{m.name}</div><RoleBadge role={m.role} /></div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{m.tasks} active · {m.done} done</div>
                  </div>
                  <div style={{ padding: "5px 12px", borderRadius: 99, fontSize: 12, fontWeight: 700, background: m.tasks > 2 ? COLORS.dangerSoft : m.tasks > 0 ? COLORS.accentSoft : COLORS.successSoft, color: m.tasks > 2 ? COLORS.danger : m.tasks > 0 ? COLORS.accent : COLORS.success }}>{m.tasks > 2 ? "Busy" : m.tasks > 0 ? "Active" : "Free"}</div>
                </div>
                <ProgressBar value={m.tasks + m.done > 0 ? Math.round((m.done / (m.tasks + m.done)) * 100) : 0} color={m.color} height={6} />
                <div style={{ marginTop: 6, fontSize: 11, color: COLORS.textMuted, marginBottom: 10 }}>{m.tasks + m.done > 0 ? Math.round((m.done / (m.tasks + m.done)) * 100) : 0}% completion rate</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {tasks.filter(t => t.assignees.includes(m.id) && t.status !== "done").map(t => {
                    const sc = STATUS_COLUMNS.find(s => s.id === t.status);
                    const latest = t.remarksList?.[t.remarksList.length - 1];
                    const lm = latest ? USERS.find(x => x.id === latest.memberId) : null;
                    return (
                      <div key={t.id} onClick={() => setSelectedTask(t)} style={{ display: "flex", flexDirection: "column", padding: "8px 10px", background: COLORS.bg, borderRadius: 9, cursor: "pointer", gap: 4 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 12, color: COLORS.textSecondary, flex: 1 }}>{t.title}</span>
                          <span style={{ fontSize: 11, color: sc?.color, fontWeight: 600, marginLeft: 8 }}>{sc?.label}</span>
                        </div>
                        {t.status === "inprogress" && latest && (
                          <div style={{ fontSize: 11, color: COLORS.warning, display: "flex", gap: 4, alignItems: "center" }}>
                            <span>💬</span>
                            {lm && <span style={{ fontWeight: 700, color: lm.color }}>{lm.name.split(" ")[0]}:</span>}
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: COLORS.textMuted }}>{latest.text}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* USERS VIEW — admin only */}
        {view === "users" && isAdmin && <UserManagementView users={users} tasks={tasks} onDeleteUser={id => setUsers(prev => prev.filter(u => u.id !== id))} onAddUser={u => setUsers(prev => [...prev, u])} currentUser={currentUser} />}

      </div>

      {/* Bottom nav */}
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 1280, background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`, padding: "10px 0 24px", display: "flex", justifyContent: "space-around" }}>
        {navTabs.map(tab => (
          <button key={tab.id} onClick={() => { setView(tab.id); setDashDrill(null); }} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, background: "transparent", border: "none", cursor: "pointer", color: view === tab.id ? COLORS.accent : COLORS.textMuted, padding: "4px 16px" }}>
            <span style={{ fontSize: 18 }}>{tab.icon}</span>
            <span style={{ fontSize: 10, fontWeight: view === tab.id ? 700 : 500 }}>{tab.label}</span>
            {view === tab.id && <div style={{ width: 4, height: 4, borderRadius: "50%", background: COLORS.accent }} />}
          </button>
        ))}
      </div>

      {selectedTask && <TaskModal task={selectedTask} users={USERS} onClose={() => setSelectedTask(null)} onUpdate={handleUpdate} currentUser={currentUser} />}
      {showAdd && <AddTaskModal users={USERS} onClose={() => setShowAdd(false)} onAdd={handleAdd} currentUser={currentUser} />}
    </div>
  );
}
